/**
 * Google Consent Mode v2 integration.
 *
 * Pushes `gtag('consent', 'default', {...})` before gtag.js loads,
 * and `gtag('consent', 'update', {...})` when consent changes.
 */
import type { CookieCategory } from "./types";

interface GtagConsentParams {
	ad_storage: "granted" | "denied";
	ad_user_data: "granted" | "denied";
	ad_personalization: "granted" | "denied";
	analytics_storage: "granted" | "denied";
	functionality_storage: "granted" | "denied";
	personalization_storage: "granted" | "denied";
	security_storage: "granted";
}

type GtagArgs = [string, string, GtagConsentParams];

function ensureDataLayer(): void {
	const g = globalThis as unknown as Record<string, unknown>;
	if (!Array.isArray(g.dataLayer)) {
		g.dataLayer = [];
	}
}

function gtag(...args: GtagArgs): void {
	ensureDataLayer();
	const g = globalThis as unknown as { dataLayer: unknown[] };
	g.dataLayer.push(args);
}

function buildConsentParams(choices: Record<CookieCategory, boolean> | null): GtagConsentParams {
	const marketing = choices?.marketing === true;
	const analytics = choices?.analytics === true;
	const functional = choices?.functional === true;

	return {
		ad_storage: marketing ? "granted" : "denied",
		ad_user_data: marketing ? "granted" : "denied",
		ad_personalization: marketing ? "granted" : "denied",
		analytics_storage: analytics ? "granted" : "denied",
		functionality_storage: functional ? "granted" : "denied",
		personalization_storage: functional ? "granted" : "denied",
		security_storage: "granted",
	};
}

/**
 * Push the default consent state BEFORE gtag.js loads.
 * Must be called as early as possible (synchronous, before any GTM/GA scripts).
 */
export function pushConsentDefault(choices: Record<CookieCategory, boolean> | null): void {
	ensureDataLayer();
	gtag("consent", "default", buildConsentParams(choices));
}

/**
 * Push a consent update when the user changes preferences.
 */
export function pushConsentUpdate(choices: Record<CookieCategory, boolean>): void {
	gtag("consent", "update", buildConsentParams(choices));
}
